// Geocodificación: Google Places API (New) PRINCIPAL + proxy backend (Nominatim/OSM) de RESPALDO.
// - Google: clave de NAVEGADOR (REACT_APP_GOOGLE_MAPS_API_KEY, restringida por HTTP referrer).
//   La clave de backend (GOOGLE_ROUTES_API_KEY) NUNCA se usa aquí.
// - Respaldo: /api/geo/autocomplete del backend (Nominatim, sin clave) si Google falla o no hay key.
// - Costos: debounce en el llamador (600ms), caché 5 min, detalles de Place SOLO al seleccionar.
// - Cobertura nacional Colombia; sesgo suave a El Cerrito/Ginebra SIN excluir otros municipios.

import { authHeaders } from "@/lib/supabase";

const API = process.env.REACT_APP_BACKEND_URL;
const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const PLACES_AUTOCOMPLETE = "https://places.googleapis.com/v1/places:autocomplete";
const PLACES_DETAILS = "https://places.googleapis.com/v1/places";
const OP_CENTER = { lat: 3.705, lng: -76.29 }; // El Cerrito/Ginebra
const LOCAL_MUNIS = ["el cerrito", "cerrito", "ginebra"];

const cache = new Map(); // qNorm -> { t, results, provider }
const CACHE_TTL_MS = 5 * 60 * 1000;

const normalize = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

// Sube resultados de El Cerrito/Ginebra sin descartar el resto (orden estable).
function prioritizeLocal(results) {
  return [...results]
    .map((r, i) => ({ r, i, local: LOCAL_MUNIS.includes(normalize(r.city || "")) }))
    .sort((a, b) => b.local - a.local || a.i - b.i)
    .map((x) => x.r);
}

// Clasifica el resultado: house = dirección con número (exacta); street = solo vía; area = zona.
function kindFromTypes(types) {
  const t = types || [];
  if (t.includes("street_address") || t.includes("premise") || t.includes("subpremise")) return "house";
  if (t.includes("route")) return "street";
  return "area";
}

async function googleAutocomplete(q, signal) {
  const res = await fetch(PLACES_AUTOCOMPLETE, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Goog-Api-Key": GKEY },
    body: JSON.stringify({
      input: q,
      languageCode: "es",
      regionCode: "co",
      includedRegionCodes: ["co"],
      locationBias: {
        circle: { center: { latitude: OP_CENTER.lat, longitude: OP_CENTER.lng }, radius: 50000 },
      },
    }),
    signal,
  });
  if (!res.ok) throw new Error("google_http_" + res.status);
  const data = await res.json();
  return (data.suggestions || [])
    .map((s) => s.placePrediction)
    .filter(Boolean)
    .slice(0, 6)
    .map((p) => ({
      label: p.text?.text || "",
      city: null, // se resuelve en los detalles al seleccionar
      kind: kindFromTypes(p.types),
      placeId: p.placeId,
      needsResolution: true, // coordenadas se obtienen SOLO al seleccionar (ahorro de cuota)
    }));
}

/**
 * resolve_place(placeId): obtiene coordenadas reales + municipio + tipo del lugar.
 * Se llama UNA sola vez, cuando el usuario selecciona la sugerencia.
 */
export async function resolve_place(placeId, signal) {
  const url = `${PLACES_DETAILS}/${placeId}?fields=location,formattedAddress,types,addressComponents&key=${GKEY}`;
  const res = await fetch(url, { signal });
  if (!res.ok) return { ok: false };
  const d = await res.json();
  const comps = d.addressComponents || [];
  const city =
    comps.find((c) => c.types?.includes("locality"))?.longText ||
    comps.find((c) => c.types?.includes("administrative_area_level_2"))?.longText ||
    null;
  if (!d.location) return { ok: false };
  return {
    ok: true,
    label: d.formattedAddress || "",
    lat: d.location.latitude,
    lng: d.location.longitude,
    city,
    kind: kindFromTypes(d.types),
  };
}

async function backendAutocomplete(q, signal) {
  const res = await fetch(`${API}/api/geo/autocomplete?text=${encodeURIComponent(q)}&limit=6`, {
    signal,
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error("backend_http_" + res.status);
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "backend_error");
  return { results: data.results || [], provider: data.provider };
}

/**
 * search_addresses(query, signal)
 * @returns {Promise<{ok:boolean, results:Array, provider?:string, error?:string, aborted?:boolean}>}
 */
export async function search_addresses(query, signal) {
  const q = (query || "").trim();
  if (q.length < 4) return { ok: true, results: [] };

  const key = normalize(q);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.t < CACHE_TTL_MS) return { ok: true, results: hit.results, provider: hit.provider };

  try {
    // 1) Google Places (New) si hay clave de navegador
    if (GKEY) {
      try {
        const results = await googleAutocomplete(q, signal);
        if (results.length > 0) {
          cache.set(key, { t: Date.now(), results, provider: "google" });
          return { ok: true, results, provider: "google" };
        }
      } catch (e) {
        if (e?.name === "AbortError") return { ok: false, aborted: true, results: [] };
        // Observabilidad: avisar una vez por consulta cuando Google falla y cae al respaldo
        console.warn("[geo] Google Places falló, usando respaldo Nominatim:", e?.message || e);
      }
    }
    // 2) Respaldo: proxy backend (Nominatim)
    const { results, provider } = await backendAutocomplete(q, signal);
    const ordered = prioritizeLocal(results);
    cache.set(key, { t: Date.now(), results: ordered, provider });
    return { ok: true, results: ordered, provider };
  } catch (e) {
    if (e?.name === "AbortError") return { ok: false, aborted: true, results: [] };
    return { ok: false, results: [], error: "No se pudo buscar la dirección. Usa «Ubicar la casa en el mapa»." };
  }
}
